import { useEffect, useState } from "react";
import {
  disableMailWebPush,
  enableMailWebPush,
  getMailWebPushStatus,
  isMailWebPushSupported,
  mailWebPushStatusLabel,
  type MailWebPushStatus,
} from "@/lib/mail/mailWebPush";
import { MAIL_INSTALL_URL, mailInstallQrDataUrl } from "@/lib/mail/mailInstallQr";

/** Opt-in Web Push + QR PWA — même UX que Chat (Profil). */
export function MailWebPushSettings() {
  const [pushStatus, setPushStatus] = useState<MailWebPushStatus>(() =>
    isMailWebPushSupported() ? "default" : "unsupported"
  );
  const [pushBusy, setPushBusy] = useState(false);
  const [qrSrc] = useState(() => mailInstallQrDataUrl());

  useEffect(() => {
    let cancelled = false;
    void getMailWebPushStatus().then((status) => {
      if (!cancelled) setPushStatus(status);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const pushOn = pushStatus === "subscribed";
  const pushLocked =
    pushBusy ||
    pushStatus === "unsupported" ||
    pushStatus === "denied" ||
    pushStatus === "not_configured";

  async function togglePush() {
    if (pushLocked) return;
    setPushBusy(true);
    try {
      const next = pushOn ? await disableMailWebPush() : await enableMailWebPush();
      setPushStatus(next.status);
    } finally {
      setPushBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[var(--mail-text)]">Notifications système</p>
          <p className="text-[10px] leading-snug text-[var(--mail-muted)]">
            {mailWebPushStatusLabel(pushStatus)}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={pushOn}
          aria-label="Notifications système"
          disabled={pushLocked}
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
            pushOn ? "bg-[var(--mail-accent)]" : "bg-[var(--mail-border)]"
          }`}
          onClick={() => void togglePush()}
        >
          <span
            className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-transform ${
              pushOn ? "left-[1.35rem]" : "left-0.5"
            }`}
          />
        </button>
      </div>

      <div className="border-t border-[var(--mail-border-soft)] pt-2.5">
        <p className="text-sm font-medium text-[var(--mail-text)]">Installer sur mobile</p>
        <p className="mt-0.5 text-[10px] leading-snug text-[var(--mail-muted)]">
          Scannez pour ouvrir Mail et l’ajouter à l’écran d’accueil
        </p>
        <div className="mt-2.5 flex justify-center rounded-lg bg-white p-2">
          <img
            src={qrSrc}
            alt={`QR code ${MAIL_INSTALL_URL}`}
            width={148}
            height={148}
            className="size-36"
          />
        </div>
        <a
          href={MAIL_INSTALL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 block truncate text-center text-[10px] text-[var(--mail-accent)] hover:underline"
        >
          {MAIL_INSTALL_URL}
        </a>
      </div>
    </div>
  );
}
